import apiClient from './_axios';

// API client for Data Plans (ISP quota pool tracker). Server endpoints
// live under /vendo/data-plans on the same router as the rest of the
// vendo product surface.

export type DataPlanMode = 'shared' | 'manual';
export type LedgerEntryType = 'base' | 'topup' | 'adjustment';

export interface DataPlanRate {
   _id: string;
   price: number;
   capMb: number;
   label: string;
   description?: string;
   // Which captive-portal tab surfaces this rate. 'coin' = matched at
   // coin-commit on a data-mode vendo; 'voucher' = surfaced in Voucher
   // tab (bulk-print + GCash). Optional in old payloads, server emits
   // it on new rows.
   method?: 'coin' | 'voucher';
   // Per-rate VALIDITY (minutes) — from the purchase. null = use device's
   // defaultValidityMinutes. 0 = never expires.
   validityMinutes?: number | null;
}

export interface DataPlanDeviceLink {
   _id: string;
   // Populated from server: { _id, name, deviceId } when fetched via
   // listDataPlans / getDataPlan. Raw ObjectId string in other shapes.
   device: { _id: string; name: string; deviceId: string } | string;
   allotmentMb: number | null;
   usedMb: number;
   /** MB handed back to this vendo's slice by ended sessions that had data left. */
   returnedMb?: number;
}

export interface DataPlanLedgerEntry {
   _id: string;
   type: LedgerEntryType;
   amountMb: number;
   note?: string;
   date: string;
}

export interface DataPlan {
   _id: string;
   owner: string;
   name: string;
   isp: string;
   mode: DataPlanMode;
   rates: DataPlanRate[];
   devices: DataPlanDeviceLink[];
   ledger: DataPlanLedgerEntry[];
   totalUsedMb: number;
   /** MB handed back to the pool by ended sessions that had data left (already inside remaining). */
   returnedMb?: number;
   // MB granted per peso for amounts that don't match a configured rate
   // exactly — leftover after the highest qualifying bundle, or the
   // entire amount when no rate qualifies. Default 100 (₱1 → 100MB).
   pesoBaseMb: number;
   createdAt: string;
   updatedAt: string;
}

// ---- Plan CRUD ----

export async function listDataPlans(): Promise<DataPlan[]> {
   const r = await apiClient.get<{ plans: DataPlan[] }>('/vendo/data-plans');
   return r.data.plans;
}

export async function getDataPlan(id: string): Promise<DataPlan> {
   const r = await apiClient.get<{ plan: DataPlan }>(`/vendo/data-plans/${id}`);
   return r.data.plan;
}

export async function createDataPlan(payload: {
   name: string; isp?: string; mode?: DataPlanMode;
}): Promise<DataPlan> {
   const r = await apiClient.post<{ plan: DataPlan }>('/vendo/data-plans', payload);
   return r.data.plan;
}

export async function updateDataPlan(
   id: string,
   payload: { name?: string; isp?: string; mode?: DataPlanMode; pesoBaseMb?: number }
): Promise<DataPlan> {
   const r = await apiClient.patch<{ plan: DataPlan }>(`/vendo/data-plans/${id}`, payload);
   return r.data.plan;
}

export async function deleteDataPlan(id: string): Promise<void> {
   await apiClient.delete(`/vendo/data-plans/${id}`);
}

// ---- Ledger ----

export async function addLedgerEntry(
   id: string,
   payload: { type: LedgerEntryType; amountMb: number; note?: string; date?: string }
): Promise<DataPlan> {
   const r = await apiClient.post<{ plan: DataPlan }>(`/vendo/data-plans/${id}/ledger`, payload);
   return r.data.plan;
}

// ---- Plan rates ----

export async function addPlanRate(
   id: string,
   payload: {
      price: number; capMb: number; label: string;
      description?: string; method?: 'coin' | 'voucher';
      validityMinutes?: number | null;
   }
): Promise<DataPlan> {
   const r = await apiClient.post<{ plan: DataPlan }>(`/vendo/data-plans/${id}/rates`, payload);
   return r.data.plan;
}

export async function updatePlanRate(
   id: string,
   rateId: string,
   payload: Partial<{
      price: number; capMb: number; label: string;
      description: string; method: 'coin' | 'voucher';
      validityMinutes: number | null;
   }>
): Promise<DataPlan> {
   const r = await apiClient.patch<{ plan: DataPlan }>(
      `/vendo/data-plans/${id}/rates/${rateId}`, payload
   );
   return r.data.plan;
}

export async function removePlanRate(id: string, rateId: string): Promise<DataPlan> {
   const r = await apiClient.delete<{ plan: DataPlan }>(`/vendo/data-plans/${id}/rates/${rateId}`);
   return r.data.plan;
}

// ---- Device links ----

export async function linkDeviceToPlan(
   id: string,
   payload: { deviceId: string; allotmentMb?: number | null }
): Promise<DataPlan> {
   const r = await apiClient.post<{ plan: DataPlan }>(`/vendo/data-plans/${id}/devices`, payload);
   return r.data.plan;
}

export async function updateDeviceAllotment(
   id: string,
   deviceId: string,
   allotmentMb: number | null
): Promise<DataPlan> {
   const r = await apiClient.patch<{ plan: DataPlan }>(
      `/vendo/data-plans/${id}/devices/${deviceId}`,
      { allotmentMb }
   );
   return r.data.plan;
}

export async function unlinkDeviceFromPlan(id: string, deviceId: string): Promise<DataPlan> {
   const r = await apiClient.delete<{ plan: DataPlan }>(
      `/vendo/data-plans/${id}/devices/${deviceId}`
   );
   return r.data.plan;
}

// ---- Computed helpers ----

/**
 * Smart-format a byte total in MB. Shows MB when <1 GB so we don't
 * end up with "0.025 GB" ugliness; switches to GB with one decimal
 * above that. Returns "0 MB" for falsy/zero inputs.
 */
export function formatMb(mb: number | null | undefined): string {
   if (!mb || mb < 0) return '0 MB';
   // Decimal MB/GB convention (1 GB = 1000 MB) — matches what
   // operators type into the admin form and what telecom plans
   // advertise. Don't switch back to 1024 here unless you also
   // switch DataPlanModal's input conversions or the displayed
   // number won't match the typed number.
   if (mb < 1000) return `${Math.round(mb)} MB`;
   const gb = mb / 1000;
   return gb >= 10 ? `${Math.round(gb)} GB` : `${gb.toFixed(1)} GB`;
}

/**
 * Pool budget = sum(ledger amounts) − totalUsedMb. Adjustment entries
 * can be negative so a simple sum is correct.
 */
export function planRemainingMb(plan: DataPlan): number {
   const ledgerSum = plan.ledger.reduce((s, e) => s + e.amountMb, 0);
   return Math.max(0, ledgerSum - plan.totalUsedMb);
}

export function planTotalMb(plan: DataPlan): number {
   return plan.ledger.reduce((s, e) => s + e.amountMb, 0);
}
